import { etiquetaVaca } from '@shared/utils/etiqueta-vaca';
import { StockVitrificado, StockVitrificadoEmbrion } from '../embriones.interface';

/**
 * Una fila de la tabla de stock: una pajuela, con las columnas del informe de PIVE
 * (CLIENTE, TERMO, CAÑAS, TÉCNICA, FECHA OPU, FECHA D7, DONANTE, RAZA, TORO, RAZA,
 * N° EMB, SERIE / ID RACK, ID OPS, FECHA TE, TE, PÉRDIDA, SALDO, ESTADO, DESTINO).
 */
export interface FilaPajuela {
  /** Id de la pajuela: sigue a la fila entre recargas. */
  id: number;
  stock: StockVitrificado;
  pajuela: StockVitrificadoEmbrion;
  cliente: string;
  termo: string;
  canas: string;
  tecnica: string;
  fechaOpu: string;
  fechaD7: string;
  donante: string;
  razaDonante: string;
  toro: string;
  razaToro: string;
  embriones: number;
  serieRack: string;
  idOps: string;
  fechaTe: string | null;
  te: number | null;
  perdida: number | null;
  saldo: number;
  estado: string;
  destino: string;
}

/**
 * VIT o DT. Las de laboratorio traen la técnica; en las manuales se deduce: con
 * marca OPS es vitrificada y solo con ID impreso es Direct Transfer.
 */
export function tecnicaDePajuela(stock: StockVitrificado, pajuela: StockVitrificadoEmbrion): string {
  if (stock.tecnica_congelamiento) return stock.tecnica_congelamiento.toUpperCase();
  if (pajuela.ops_punto || pajuela.ops_color) return 'VIT';

  return pajuela.identificador ? 'DT' : '';
}

/** ID OPS del informe: "1.1 rojo" en las vitrificadas; en las DT, el ID impreso. */
export function idOpsDePajuela(pajuela: StockVitrificadoEmbrion): string {
  const marca = [pajuela.ops_punto?.codigo, pajuela.ops_color?.nombre].filter(Boolean).join(' ');

  return marca || pajuela.identificador || '';
}

export function estadoDePajuela(pajuela: StockVitrificadoEmbrion): string {
  if (pajuela.estado === 'consumida') return pajuela.motivo_salida_label || 'Consumida';

  return pajuela.estado === 'reservada' ? 'Reservada' : 'Disponible';
}

export function filaDePajuela(stock: StockVitrificado, pajuela: StockVitrificadoEmbrion): FilaPajuela {
  const transferida = pajuela.consumida && pajuela.motivo_salida === 'T';
  const porEmbrion = pajuela.embriones_transferidos != null;

  return {
    id: pajuela.id,
    stock,
    pajuela,
    cliente: stock.cliente?.persona?.nombre_generico ?? '',
    termo: pajuela.termo ?? '',
    canas: pajuela.canas ?? '',
    tecnica: tecnicaDePajuela(stock, pajuela),
    fechaOpu: stock.fecha_opu,
    fechaD7: stock.fecha_vit,
    donante: etiquetaVaca(stock.donante),
    razaDonante: stock.donante?.raza_nombre || stock.donante?.raza_codigo || '',
    toro: stock.toro?.nombre ?? '',
    razaToro: stock.toro?.raza_nombre || stock.toro?.raza_codigo || '',
    embriones: pajuela.nro_embriones,
    serieRack: stock.raque ?? '',
    idOps: idOpsDePajuela(pajuela),
    fechaTe: transferida ? pajuela.fecha_consumida : null,
    // 2026-09-30: gastada en TE se cuenta por embrión (2 transferidos y 4 sobrantes de una pajuela de 6).
    te: porEmbrion ? pajuela.embriones_transferidos || null : transferida ? pajuela.nro_embriones : null,
    perdida: porEmbrion
      ? pajuela.embriones_perdidos || null
      : pajuela.consumida && pajuela.motivo_salida === 'P' ? pajuela.nro_embriones : null,
    // Lo que sigue en el botijón: lo reservado todavía no salió.
    saldo: pajuela.consumida ? 0 : pajuela.nro_embriones,
    estado: estadoDePajuela(pajuela),
    destino: pajuela.destino ?? '',
  };
}

/** Todas las pajuelas de los stocks, las más nuevas primero (por D7, después por orden). */
export function filasDePajuelas(stocks: StockVitrificado[]): FilaPajuela[] {
  return stocks
    .flatMap(stock => (stock.embriones ?? []).map(pajuela => filaDePajuela(stock, pajuela)))
    .sort((a, b) => b.fechaD7.localeCompare(a.fechaD7) || b.stock.id - a.stock.id || a.pajuela.orden - b.pajuela.orden);
}
